import React, { useEffect, useRef, useState } from "react";
import { Grid, Header, Button, Message, Segment, Divider, Icon } from "semantic-ui-react";
import { useFormikContext } from "formik";
import { toast } from "react-toastify";
import MyTextInput from "../../app/common/form/MyTextInput";
import MyCheckbox from "../../app/common/form/MyCheckbox";
import {
  functionGetGlobalStrategySettings,
  functionAdminUpdateGlobalStrategySettings,
  functionAdminGrantStrategyCredits,
  functionAdminGetUserStrategyCredits,
  functionAdminSetUserFeedbackCreditOverride,
  functionAdminGrantManagerPoolCredits,
  functionAdminGetManagerPoolStrategyCredits,
  functionAdminBackfillPaidAgentStrategyCredits,
} from "../../app/firestore/functionsService";
import { getUserProfileByEmailFromDb, fetchAllManagersFromDb } from "../../app/firestore/firestoreService";

// Role-based default for the "AI Strategies" section of the New User form
// (spec sections 9-10). Mirrors the FreeTrialSetter pattern elsewhere in
// this file (a useFormikContext consumer that imperatively sets fields) -
// Formik's initialValues alone can't react to the role dropdown changing
// after mount.
export function AiStrategiesNewUserSection() {
  const { values, setFieldValue } = useFormikContext();
  const [globalSettings, setGlobalSettings] = useState(null);
  const previousRole = useRef(null);

  useEffect(() => {
    let cancelled = false;
    functionGetGlobalStrategySettings()
      .then((s) => { if (!cancelled) setGlobalSettings(s); })
      .catch(() => { if (!cancelled) setGlobalSettings({ paidAgentStartingFreeCredits: 3 }); });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!globalSettings) return;
    if (previousRole.current === values.role) return;
    previousRole.current = values.role;

    // "coagent" is an agent-tier account role in this app (not just a
    // per-transaction label) - treated the same as "agent" here, matching
    // the server-side default in functions/strategyCredits/initialGrant.js.
    const roleDefault = ["agent", "coagent"].includes(values.role) ? globalSettings.paidAgentStartingFreeCredits : 0;
    setFieldValue("freeStrategyCredits", roleDefault);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.role, globalSettings]);

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column mobile={16} computer={5}>
          <MyTextInput name="freeStrategyCredits" label="Free Strategy Credits" type="number" min={0} />
        </Grid.Column>
        <Grid.Column mobile={16} computer={11} style={{ display: "flex", alignItems: "flex-end" }}>
          <MyCheckbox
            name="strategyTrialAvailable"
            label="Make these Strategy Credits available during the user's free trial"
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
}

// Admin-configurable global defaults (spec sections 11/77) - no existing
// admin-settings collection/UI existed anywhere in this codebase before
// this; modeled on FhfaHpiAdminPanel.jsx's standalone-panel shape.
export default function GlobalStrategySettingsAdminPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    functionGetGlobalStrategySettings()
      .then((s) => { if (!cancelled) setSettings(s); })
      .catch((err) => { if (!cancelled) toast.error(err.message); });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      const updated = await functionAdminUpdateGlobalStrategySettings({
        paidAgentStartingFreeCredits: Number(settings.paidAgentStartingFreeCredits) || 0,
        maxFeedbackCreditsPerUser: Number(settings.maxFeedbackCreditsPerUser) || 0,
        managerCreditPackages: (settings.managerCreditPackages || []).map((pkg) => ({
          key: pkg.key,
          label: pkg.label,
          credits: Number(pkg.credits) || 0,
          stripePriceId: pkg.stripePriceId || null,
        })),
      });
      setSettings(updated);
      toast.success("Global Intelligent Strategy settings saved.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  function updateManagerPackage(index, field, value) {
    const packages = [...(settings.managerCreditPackages || [])];
    packages[index] = { ...packages[index], [field]: value };
    setSettings({ ...settings, managerCreditPackages: packages });
  }

  function removeManagerPackage(index) {
    const packages = [...(settings.managerCreditPackages || [])];
    packages.splice(index, 1);
    setSettings({ ...settings, managerCreditPackages: packages });
  }

  function addManagerPackage() {
    const packages = [...(settings.managerCreditPackages || [])];
    packages.push({ key: "", label: "", credits: "", stripePriceId: "" });
    setSettings({ ...settings, managerCreditPackages: packages });
  }

  if (!settings) return null;

  return (
    <Segment clearing>
      <Header size="huge" color="blue">
        Global Intelligent Strategy Settings
      </Header>
      <Message info size="small">
        These defaults apply to newly-created users going forward only - changing them never claws back
        or tops up credits already granted to existing users.
      </Message>
      <Grid>
        <Grid.Row>
          <Grid.Column mobile={16} computer={5}>
            <label className="bold">Paid Agent Starting Free Credits</label>
            <input
              className="ui input"
              type="number"
              min={0}
              value={settings.paidAgentStartingFreeCredits}
              onChange={(e) => setSettings({ ...settings, paidAgentStartingFreeCredits: e.target.value })}
            />
          </Grid.Column>
          <Grid.Column mobile={16} computer={5}>
            <label className="bold">Maximum Feedback Credits Per User</label>
            <input
              className="ui input"
              type="number"
              min={0}
              value={settings.maxFeedbackCreditsPerUser}
              onChange={(e) => setSettings({ ...settings, maxFeedbackCreditsPerUser: e.target.value })}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Divider />
      <Header as="h4" color="blue">
        Manager Strategy Credit Packages (Payments page)
      </Header>
      <Message info size="small">
        Each package needs a Stripe Price ID before it will show up for Managers to buy - see "Setting this
        up in Stripe" below. Create ONE Stripe Product (e.g. "Manager AI Strategy Credits") with one Price
        per package - do not create a separate Product per package.
      </Message>
      {(settings.managerCreditPackages || []).map((pkg, index) => (
        <Grid key={index} style={{ marginBottom: 4 }}>
          <Grid.Row>
            <Grid.Column mobile={16} computer={4}>
              <label className="bold">Label</label>
              <input
                className="ui input"
                placeholder="100 AI Strategy Credits"
                value={pkg.label}
                onChange={(e) => updateManagerPackage(index, "label", e.target.value)}
              />
            </Grid.Column>
            <Grid.Column mobile={16} computer={3}>
              <label className="bold">Credits</label>
              <input
                className="ui input"
                type="number"
                min={1}
                value={pkg.credits}
                onChange={(e) => updateManagerPackage(index, "credits", e.target.value)}
              />
            </Grid.Column>
            <Grid.Column mobile={16} computer={3}>
              <label className="bold">Package Key</label>
              <input
                className="ui input"
                placeholder="manager_100"
                value={pkg.key}
                onChange={(e) => updateManagerPackage(index, "key", e.target.value)}
              />
            </Grid.Column>
            <Grid.Column mobile={16} computer={4}>
              <label className="bold">Stripe Price ID</label>
              <input
                className="ui input"
                placeholder="price_..."
                value={pkg.stripePriceId || ""}
                onChange={(e) => updateManagerPackage(index, "stripePriceId", e.target.value)}
              />
            </Grid.Column>
            <Grid.Column mobile={16} computer={2} style={{ display: "flex", alignItems: "flex-end" }}>
              <Button basic negative size="small" onClick={() => removeManagerPackage(index)}>
                Remove
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      ))}
      <Button basic icon labelPosition="left" size="small" onClick={addManagerPackage} className="small bottom margin">
        <Icon name="plus" />
        Add Package
      </Button>

      <Divider hidden />
      <Button primary loading={saving} disabled={saving} onClick={handleSave}>
        Save Settings
      </Button>
    </Segment>
  );
}

const GRANT_REASON_OPTIONS = [
  { value: "purchase", label: "Purchased Strategy Credit" },
  { value: "promotion", label: "Promotional Credit" },
  { value: "comp", label: "Customer Service Adjustment" },
  { value: "refund", label: "Refund / Correction" },
  { value: "other", label: "Other" },
];

// Admin credit management (spec sections 37/57/77) - search a user by
// email, view their Free/Comped + Paid balances, grant or deduct (a
// negative quantity deducts, applyLedgerEntryInTransaction never lets a
// balance go negative).
export function StrategyCreditManagementPanel() {
  const [email, setEmail] = useState("");
  const [foundUser, setFoundUser] = useState(null);
  const [balance, setBalance] = useState(null);
  const [searching, setSearching] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [category, setCategory] = useState("free_comped");
  const [reason, setReason] = useState("purchase");
  const [paymentReference, setPaymentReference] = useState("");
  const [granting, setGranting] = useState(false);

  async function refreshBalance(userId) {
    const bal = await functionAdminGetUserStrategyCredits({ userId });
    setBalance(bal);
  }

  async function handleSearch() {
    setSearching(true);
    setFoundUser(null);
    setBalance(null);
    try {
      const matches = await getUserProfileByEmailFromDb(email.trim());
      if (matches.length === 0) {
        toast.error("No user found with that email.");
        return;
      }
      const user = matches[0];
      setFoundUser(user);
      await refreshBalance(user.userId || user.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSearching(false);
    }
  }

  async function handleGrant() {
    setGranting(true);
    try {
      const userId = foundUser.userId || foundUser.id;
      await functionAdminGrantStrategyCredits({
        userId,
        quantity: Number(quantity),
        creditCategory: category,
        reason,
        paymentReference: paymentReference || null,
      });
      await refreshBalance(userId);
      toast.success("Strategy Credits updated.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGranting(false);
    }
  }

  return (
    <Segment clearing>
      <Header size="huge" color="blue">
        Strategy Credit Management
      </Header>
      <Grid>
        <Grid.Row>
          <Grid.Column mobile={16} computer={8}>
            <label className="bold">Search User by Email</label>
            <input className="ui input" style={{ width: "100%" }} value={email} onChange={(e) => setEmail(e.target.value)} />
          </Grid.Column>
          <Grid.Column mobile={16} computer={4} style={{ display: "flex", alignItems: "flex-end" }}>
            <Button loading={searching} disabled={searching || !email} onClick={handleSearch}>
              Search
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {foundUser && balance && (
        <>
          <Divider />
          <p className="bold">
            {foundUser.firstName} {foundUser.lastName} ({foundUser.role})
          </p>
          <p>
            Free: {balance.freeComped}
            <br />
            Paid: {balance.paid}
            <br />
            Total: {balance.total}
          </p>
          <Divider />
          <Grid>
            <Grid.Row>
              <Grid.Column mobile={16} computer={4}>
                <label className="bold">Grant / Deduct</label>
                <input
                  className="ui input"
                  type="number"
                  style={{ width: "100%" }}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </Grid.Column>
              <Grid.Column mobile={16} computer={4}>
                <label className="bold">Category</label>
                <select
                  className="ui dropdown"
                  style={{ width: "100%", padding: "10px" }}
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="free_comped">Free / Comped</option>
                  <option value="paid">Paid</option>
                </select>
              </Grid.Column>
              <Grid.Column mobile={16} computer={4}>
                <label className="bold">Reason</label>
                <select
                  className="ui dropdown"
                  style={{ width: "100%", padding: "10px" }}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                >
                  {GRANT_REASON_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Grid.Column>
              <Grid.Column mobile={16} computer={4} style={{ display: "flex", alignItems: "flex-end" }}>
                <Button primary loading={granting} disabled={granting || !quantity} onClick={handleGrant}>
                  Grant / Deduct
                </Button>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row className="zero top padding">
              <Grid.Column mobile={16} computer={8}>
                <label className="bold">Payment Reference (optional)</label>
                <input
                  className="ui input"
                  style={{ width: "100%" }}
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </>
      )}
    </Segment>
  );
}

// Per-user credit grant, embedded directly in the Edit User modal - unlike
// StrategyCreditManagementPanel above (a standalone, separately-searched
// panel elsewhere on this page), this takes the user already being edited
// as a prop, so an admin never has to leave the Edit User flow to look the
// same person up again by email just to adjust their Strategy Credits.
export function StrategyCreditsEditUserSection({ userId, userLabel }) {
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [category, setCategory] = useState("free_comped");
  const [reason, setReason] = useState("purchase");
  const [paymentReference, setPaymentReference] = useState("");
  const [granting, setGranting] = useState(false);
  const [feedbackCapInput, setFeedbackCapInput] = useState("");
  const [savingFeedbackCap, setSavingFeedbackCap] = useState(false);

  useEffect(() => {
    if (!userId) {
      setBalance(null);
      return;
    }
    let cancelled = false;
    setLoadingBalance(true);
    functionAdminGetUserStrategyCredits({ userId })
      .then((b) => {
        if (cancelled) return;
        setBalance(b);
        setFeedbackCapInput(typeof b.maxFeedbackCreditsOverride === "number" ? String(b.maxFeedbackCreditsOverride) : "");
      })
      .catch((err) => { if (!cancelled) toast.error(err.message); })
      .finally(() => { if (!cancelled) setLoadingBalance(false); });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  async function handleGrant() {
    setGranting(true);
    try {
      await functionAdminGrantStrategyCredits({
        userId,
        quantity: Number(quantity),
        creditCategory: category,
        reason,
        paymentReference: paymentReference || null,
      });
      const updated = await functionAdminGetUserStrategyCredits({ userId });
      setBalance(updated);
      toast.success("Strategy Credits updated.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGranting(false);
    }
  }

  // Sets (or, with a blank input, clears) this specific user's feedback-
  // credit cap - e.g. re-enabling a single user from a brokerage the
  // Backfill panel excepted with maxFeedbackCreditsOverride: 0, on a
  // case-by-case basis, without affecting anyone else that exception applies to.
  async function handleSaveFeedbackCap() {
    setSavingFeedbackCap(true);
    try {
      const trimmed = feedbackCapInput.trim();
      const override = trimmed === "" ? null : Number(trimmed);
      const updated = await functionAdminSetUserFeedbackCreditOverride({ userId, maxFeedbackCreditsOverride: override });
      setBalance((prev) => ({ ...prev, maxFeedbackCreditsOverride: updated.maxFeedbackCreditsOverride }));
      toast.success("Feedback credit cap updated.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingFeedbackCap(false);
    }
  }

  if (!userId) return null;

  return (
    <Segment>
      <Header size="medium" color="blue">
        Strategy Credits{userLabel ? ` — ${userLabel}` : ""}
      </Header>
      {loadingBalance && <p className="text-grey">Loading balance...</p>}
      {!loadingBalance && balance && (
        <p>
          Free: {balance.freeComped}
          <br />
          Paid: {balance.paid}
          <br />
          Total: {balance.total}
        </p>
      )}
      <Divider />
      <Grid>
        <Grid.Row>
          <Grid.Column mobile={16} computer={4}>
            <label className="bold">Grant / Deduct</label>
            <input
              className="ui input"
              type="number"
              style={{ width: "100%" }}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </Grid.Column>
          <Grid.Column mobile={16} computer={4}>
            <label className="bold">Category</label>
            <select
              className="ui dropdown"
              style={{ width: "100%", padding: "10px" }}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="free_comped">Free / Comped</option>
              <option value="paid">Paid</option>
            </select>
          </Grid.Column>
          <Grid.Column mobile={16} computer={4}>
            <label className="bold">Reason</label>
            <select
              className="ui dropdown"
              style={{ width: "100%", padding: "10px" }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {GRANT_REASON_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Grid.Column>
          <Grid.Column mobile={16} computer={4} style={{ display: "flex", alignItems: "flex-end" }}>
            <Button
              primary
              type="button"
              loading={granting}
              disabled={granting || !quantity}
              onClick={handleGrant}
            >
              Grant / Deduct
            </Button>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row className="zero top padding">
          <Grid.Column mobile={16} computer={8}>
            <label className="bold">Payment Reference (optional)</label>
            <input
              className="ui input"
              style={{ width: "100%" }}
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Divider />
      <Header size="tiny" color="blue">
        Feedback Credit Cap
      </Header>
      <p className="text-grey" style={{ fontSize: "0.85em" }}>
        Blank uses the global Maximum Feedback Credits Per User setting. A brokerage excepted via the Backfill
        panel gets 0 here automatically - it's not permanent, and can be raised (or cleared) for this specific
        user any time.
      </p>
      <Grid>
        <Grid.Row className="zero top padding zero bottom padding">
          <Grid.Column mobile={16} computer={4}>
            <input
              className="ui input"
              type="number"
              min={0}
              style={{ width: "100%" }}
              placeholder="Global default"
              value={feedbackCapInput}
              onChange={(e) => setFeedbackCapInput(e.target.value)}
            />
          </Grid.Column>
          <Grid.Column mobile={16} computer={4}>
            <Button
              type="button"
              loading={savingFeedbackCap}
              disabled={savingFeedbackCap}
              onClick={handleSaveFeedbackCap}
            >
              Save Cap
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
}

// Manager shared-pool credit fulfillment (spec sections 51/77) - manual,
// since the real Stripe integration for the 10-credit manager package
// doesn't exist yet (see the plan's "Automating Stripe fulfillment" note).
export function ManagerPoolAdminPanel() {
  const [managers, setManagers] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState("");
  const [balance, setBalance] = useState(null);
  const [quantity, setQuantity] = useState(10);
  const [paymentReference, setPaymentReference] = useState("");
  const [granting, setGranting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchAllManagersFromDb()
      .then((m) => { if (!cancelled) setManagers(m); })
      .catch((err) => { if (!cancelled) toast.error(err.message); });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedManagerId) {
      setBalance(null);
      return;
    }
    let cancelled = false;
    functionAdminGetManagerPoolStrategyCredits({ managerUserId: selectedManagerId })
      .then((b) => { if (!cancelled) setBalance(b); })
      .catch((err) => { if (!cancelled) toast.error(err.message); });
    return () => {
      cancelled = true;
    };
  }, [selectedManagerId]);

  async function handleGrant() {
    setGranting(true);
    try {
      await functionAdminGrantManagerPoolCredits({
        managerUserId: selectedManagerId,
        quantity: Number(quantity),
        paymentReference: paymentReference || null,
      });
      const bal = await functionAdminGetManagerPoolStrategyCredits({ managerUserId: selectedManagerId });
      setBalance(bal);
      toast.success("Manager Strategy Credits updated.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGranting(false);
    }
  }

  return (
    <Segment clearing>
      <Header size="huge" color="blue">
        Manager Pool Admin
      </Header>
      <Grid>
        <Grid.Row>
          <Grid.Column mobile={16} computer={6}>
            <label className="bold">Manager</label>
            <select
              className="ui dropdown"
              style={{ width: "100%", padding: "10px" }}
              value={selectedManagerId}
              onChange={(e) => setSelectedManagerId(e.target.value)}
            >
              <option value="">Select a manager...</option>
              {managers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.firstName} {m.lastName}
                </option>
              ))}
            </select>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {selectedManagerId && balance && (
        <>
          <Divider />
          <p>Shared Paid Credits: {balance.total} available</p>
          <Divider />
          <Grid>
            <Grid.Row>
              <Grid.Column mobile={16} computer={4}>
                <label className="bold">Grant</label>
                <input
                  className="ui input"
                  type="number"
                  style={{ width: "100%" }}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </Grid.Column>
              <Grid.Column mobile={16} computer={6}>
                <label className="bold">Payment Reference (optional)</label>
                <input
                  className="ui input"
                  style={{ width: "100%" }}
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                />
              </Grid.Column>
              <Grid.Column mobile={16} computer={4} style={{ display: "flex", alignItems: "flex-end" }}>
                <Button primary loading={granting} disabled={granting || !quantity} onClick={handleGrant}>
                  Grant Credits
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </>
      )}
    </Segment>
  );
}

// One-time backfill for users who were already paid before this credit
// system existed - they never went through createNewUser's
// strategyCreditConfig setup, so the normal grant flow has nothing to act
// on for them without this. Safe to re-run (idempotent) - only ever grants
// once per user.
export function BackfillPaidAgentCreditsPanel() {
  const [quantity, setQuantity] = useState(5);
  const [excludedBrokerageForms, setExcludedBrokerageForms] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  async function handleRun() {
    setRunning(true);
    setResult(null);
    try {
      const summary = await functionAdminBackfillPaidAgentStrategyCredits({
        quantity: Number(quantity),
        excludedBrokerageForms: excludedBrokerageForms.trim() || undefined,
      });
      setResult(summary);
      toast.success(`Backfill complete: ${summary.granted} users granted ${summary.quantity} credits each.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRunning(false);
    }
  }

  return (
    <Segment clearing>
      <Header size="huge" color="blue">
        Backfill Paid Agent Strategy Credits
      </Header>
      <p>
        Grants the credits below (default 5 - a one-time welcome amount, separate from the Global
        Intelligent Strategy Settings default used for brand-new signups) to every CURRENT paid Agent/CoAgent
        account that hasn't received an initial grant yet. Safe to run more than once - never grants twice to
        the same user, and never touches Assistant/TC/Manager/ManagerAssistant accounts (they still default
        to 0).
      </p>
      <Grid>
        <Grid.Row className="zero bottom padding">
          <Grid.Column mobile={16} computer={5}>
            <label className="bold">Credits to Grant</label>
            <input
              className="ui input"
              type="number"
              min={0}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </Grid.Column>
          <Grid.Column mobile={16} computer={11} style={{ display: "flex", alignItems: "flex-end" }}>
            <Button primary loading={running} disabled={running} onClick={handleRun}>
              Run Backfill Now
            </Button>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row className="zero top padding">
          <Grid.Column mobile={16} computer={11}>
            <label className="bold">Exclude Brokerage Forms Value (optional)</label>
            <p className="text-grey" style={{ fontSize: "0.85em", marginTop: "2px", marginBottom: "4px" }}>
              Paid agents whose "Brokerage Forms" field exactly matches this value (case/whitespace-insensitive)
              are excepted from this backfill - they receive 0 starting credits, and are also permanently
              excluded from earning Strategy Credits via feedback (regardless of the global Maximum Feedback
              Credits Per User setting).
            </p>
            <input
              className="ui input"
              style={{ width: "100%" }}
              value={excludedBrokerageForms}
              onChange={(e) => setExcludedBrokerageForms(e.target.value)}
              placeholder="e.g. Acme Realty"
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      {result && (
        <Message info size="small" style={{ marginTop: "16px" }}>
          Scanned {result.scanned} Agent/CoAgent accounts, {result.paidUsers} are paid. Granted{" "}
          {result.quantity} credits each to {result.granted} users. Already applied previously:{" "}
          {result.alreadyApplied}. Skipped: {result.skipped}.
          {result.excludedBrokerageForms &&
            ` ${result.excepted} user(s) matched the "${result.excludedBrokerageForms}" exception and received 0 credits (also excluded from feedback credits).`}
          {result.errors.length > 0 && ` Errors: ${result.errors.length}.`}
        </Message>
      )}
    </Segment>
  );
}
